/**
 * **Übernehmen** — wie aus dem Text eines **Canvas-Elements** eine Aufgabe
 * wird. Siehe „Übernehmen" in CONTEXT.md.
 *
 * Bewusst ohne React-Bezug, wie die übrigen `utils/`: Die Regel soll ohne
 * gerenderten Canvas prüfbar sein — und sie wird an ZWEI Stellen gebraucht,
 * für den Zustand des Knopfes und für die Übernahme selbst. Zwei Fassungen
 * derselben Regel liefen unweigerlich auseinander.
 */

import type { CanvasNote } from '../db/models'

export interface NoteTaskFields {
  title: string
  description: string
}

/**
 * Die Felder der Aufgabe, oder `null`, wenn aus diesem Element keine werden
 * kann.
 *
 * **Die erste nicht-leere Zeile wird der Titel, der Rest die Beschreibung** —
 * sofern das Element selbst keinen Titel trägt (siehe unten). Eine Notiz ist
 * oft ein Satz und manchmal ein Absatz; den ganzen Absatz in den Titel zu
 * legen ergäbe eine Zeile, die in der Liste niemand liest, und ihn
 * abzuschneiden verlöre den Rest. Die Trennung an der ersten Zeile ist die,
 * die der Schreibende ohnehin gemacht hat.
 *
 * Das **Emoji** bleibt auf dem Canvas: Es ist Schmuck des Zettels, und im
 * Titel einer Aufgabe wäre es ein Fremdkörper.
 */
export function noteToTaskFields(note: CanvasNote): NoteTaskFields | null {
  /*
    **Der Titel gewinnt, wenn es einen gibt.** Wer ihn gesetzt hat, hat damit
    gesagt, worum es geht; die erste Zeile des Textes zu nehmen überginge diese
    Entscheidung — bei einer Liste hieße die Aufgabe dann nach ihrem ersten
    Punkt.

    Nur die **Memo** kann einen tragen; für den **Notizen** ist `title` immer
    `null`, sein Weg unten bleibt unberührt.

    Ein LEERER Titel zählt nicht: Er heißt „Bereich an, nichts getippt"
    (CONTEXT.md, „Notiz (Canvas)"), und ein Aufgabentitel aus Leerraum wäre
    keiner.
  */
  const title = note.title?.trim()
  if (title) {
    return { title, description: note.content.trim() }
  }

  const lines = note.content.split('\n')
  const firstIndex = lines.findIndex(line => line.trim().length > 0)
  if (firstIndex === -1) return null

  const description = lines.slice(firstIndex + 1).join('\n').trim()

  return { title: lines[firstIndex]!.trim(), description }
}

/**
 * Kann dieses Element JETZT übernommen werden?
 *
 * Zwei Gründe sprechen dagegen: kein Text, oder es ist schon übernommen. Aus
 * beiden folgt dasselbe — es zählt nicht als Kandidat, und ein Knopf, für den
 * es keine Kandidaten gibt, ist deaktiviert. **Lieber tot als folgenlos:** Ein
 * Klick, der nichts tut, ist von einem kaputten Knopf nicht zu unterscheiden.
 */
export function isConvertible(note: CanvasNote): boolean {
  // Der **Text** wird nie übernommen — er ist keine Äußerung
  // (entschieden 2026-09-23, CONTEXT.md „Text"). Der Bereich ebenso wenig.
  if (note.kind === 'text' || note.kind === 'area') return false
  return note.taskId === null && noteToTaskFields(note) !== null
}

/** Trägt dieses Element ein **Eselsohr**? */
export function isConverted(note: CanvasNote): boolean {
  return note.taskId !== null
}

/**
 * Wurde der Text seit der **Übernahme** geändert?
 *
 * Ein Vergleich und kein Zeitstempel: ⌘Z ist auf dem Canvas ein Alltagsweg,
 * und wer eine Änderung zurücknimmt, hat nichts geändert. Ein Zeitstempel
 * behauptete das Gegenteil und ginge nie wieder aus.
 */
export function isEditedSinceConversion(note: CanvasNote): boolean {
  return isConverted(note)
    && note.convertedContent !== null
    && note.content !== note.convertedContent
}
